import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface OILeader {
  symbol: string
  oi_chg_pct: number
  total_oi: number
  score: number
  verdict: string
  spot: number
  pcr: number
}

interface IVLeader {
  symbol: string
  atm_iv: number
  spot: number
  pcr: number
  verdict: string
}

interface ScreenerData {
  scan_time: string
  oi_leaders: OILeader[]
  iv_leaders: IVLeader[]
  total_stocks: number
  locked?: boolean
  message?: string
}

type ScreenerTab = 'oi' | 'iv'

export default function Screener() {
  const [data, setData]       = useState<ScreenerData | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState<ScreenerTab>('oi')
  const [lastUpdate, setLastUpdate] = useState('')
  const navigate = useNavigate()

  const fetchData = async () => {
    try {
      const res = await api.get('/api/signals/screener')
      setData(res.data)
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch screener', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const verdictColor = (verdict: string) => {
    if (verdict === 'CONTEXT_BULLISH' || verdict === 'CONTEXT_BEARISH') return '#94a3b8'
    if (verdict.includes('BULLISH')) return '#22c55e'
    if (verdict.includes('BEARISH')) return '#ef4444'
    if (verdict === 'CONFLICTING') return '#f59e0b'
    return '#6b7280'
  }

  const verdictLabel = (verdict: string) => {
    if (verdict === 'STRONG_BULLISH') return '▲▲'
    if (verdict === 'BULLISH') return '▲'
    if (verdict === 'STRONG_BEARISH') return '▼▼'
    if (verdict === 'BEARISH') return '▼'
    if (verdict === 'CONTEXT_BULLISH') return '△'
    if (verdict === 'CONTEXT_BEARISH') return '▽'
    if (verdict === 'CONFLICTING') return '⚠'
    return '—'
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <span style={styles.title}>F&O Screener</span>
          {data?.total_stocks && (
            <span style={{ color: '#4b5563', fontSize: 12 }}>
              {data.total_stocks} stocks
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {lastUpdate && <span style={styles.updateTime}>Updated {lastUpdate}</span>}
          <button style={styles.refreshBtn} onClick={fetchData}>↻ Refresh</button>
        </div>
      </div>

      {/* Sub tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(tab === 'oi' ? styles.tabActive : {}) }}
          onClick={() => setTab('oi')}
        >
          📊 OI Changes
        </button>
        <button
          style={{ ...styles.tab, ...(tab === 'iv' ? styles.tabActive : {}) }}
          onClick={() => setTab('iv')}
        >
          🌡️ IV Rank
        </button>
      </div>

      {loading ? (
        <div style={styles.empty}>Loading screener data...</div>
      ) : !data ? (
        <div style={styles.empty}>Failed to load data</div>
      ) : data.locked ? (
        <div style={styles.locked}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Pro Feature</div>
          <div style={{ color: '#6b7280', marginBottom: 24 }}>
            F&O Screener requires Pro or Elite plan
          </div>
          <button style={styles.upgradeBtn} onClick={() => navigate('/upgrade')}>
            Upgrade to Pro — ₹999/month
          </button>
        </div>
      ) : (
        <div style={styles.content}>

          {/* OI Leaders tab */}
          {tab === 'oi' && (
            <>
              <div style={styles.sectionDesc}>
                Stocks ranked by biggest OI change vs morning baseline.
                Large OI moves signal institutional activity.
              </div>
              <div style={styles.table}>
                <div style={styles.tableHeader}>
                  <span style={styles.th}>#</span>
                  <span style={styles.th}>Symbol</span>
                  <span style={styles.th}>OI Change</span>
                  <span style={styles.th}>Spot</span>
                  <span style={styles.th}>PCR</span>
                  <span style={styles.th}>Verdict</span>
                  <span style={styles.th}>Score</span>
                </div>
                {data.oi_leaders.map((s, i) => (
                  <div key={s.symbol} style={{
                    ...styles.tableRow,
                    background: i % 2 === 0 ? '#0d1117' : '#111827',
                  }}>
                    <span style={{ ...styles.td, color: '#4b5563' }}>{i + 1}</span>
                    <span style={{ ...styles.td, color: '#fff', fontWeight: 700 }}>
                      {s.symbol}
                    </span>
                    <span style={{
                      ...styles.td, fontWeight: 700,
                      color: s.oi_chg_pct > 0 ? '#22c55e' : '#ef4444',
                    }}>
                      {s.oi_chg_pct > 0 ? '+' : ''}{s.oi_chg_pct}%
                    </span>
                    <span style={styles.td}>
                      ₹{s.spot.toLocaleString('en-IN')}
                    </span>
                    <span style={styles.td}>{s.pcr || '—'}</span>
                    <span style={{
                      ...styles.td, fontWeight: 700,
                      color: verdictColor(s.verdict),
                    }}>
                      {verdictLabel(s.verdict)}
                    </span>
                    <span style={{ ...styles.td, color: '#00d4ff' }}>
                      {Math.min(s.score, 10)}/10
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* IV Leaders tab */}
          {tab === 'iv' && (
            <>
              <div style={styles.sectionDesc}>
                Stocks ranked by current ATM implied volatility.
                High IV = expensive options (sell premium).
                Low IV = cheap options (buy premium with direction).
              </div>
              <div style={styles.table}>
                <div style={{
                  ...styles.tableHeader,
                  gridTemplateColumns: '40px 1fr 100px 100px 80px 60px',
                }}>
                  <span style={styles.th}>#</span>
                  <span style={styles.th}>Symbol</span>
                  <span style={styles.th}>ATM IV</span>
                  <span style={styles.th}>Spot</span>
                  <span style={styles.th}>PCR</span>
                  <span style={styles.th}>Verdict</span>
                </div>
                {data.iv_leaders.map((s, i) => (
                  <div key={s.symbol} style={{
                    ...styles.tableRow,
                    gridTemplateColumns: '40px 1fr 100px 100px 80px 60px',
                    background: i % 2 === 0 ? '#0d1117' : '#111827',
                  }}>
                    <span style={{ ...styles.td, color: '#4b5563' }}>{i + 1}</span>
                    <span style={{ ...styles.td, color: '#fff', fontWeight: 700 }}>
                      {s.symbol}
                    </span>
                    <span style={{
                      ...styles.td, fontWeight: 700,
                      color: s.atm_iv > 40 ? '#ef4444' :
                             s.atm_iv > 25 ? '#f59e0b' : '#22c55e',
                    }}>
                      {s.atm_iv}%
                      <span style={{ fontSize: 10, marginLeft: 4, color: '#4b5563' }}>
                        {s.atm_iv > 40 ? 'HIGH' : s.atm_iv > 25 ? 'MED' : 'LOW'}
                      </span>
                    </span>
                    <span style={styles.td}>
                      ₹{s.spot.toLocaleString('en-IN')}
                    </span>
                    <span style={styles.td}>{s.pcr || '—'}</span>
                    <span style={{
                      ...styles.td, fontWeight: 700,
                      color: verdictColor(s.verdict),
                    }}>
                      {verdictLabel(s.verdict)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ color: '#4b5563', fontSize: 11, marginTop: 12, padding: '0 4px' }}>
                IV context: &gt;40% = elevated (sell premium) | 15-25% = normal | &lt;15% = low (buy options)
              </div>
            </>
          )}

        </div>
      )}

      <div style={styles.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { background: '#0a0e1a', minHeight: '100vh', color: '#fff' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid #1f2937', background: '#0d1117',
    flexWrap: 'wrap' as const, gap: 8,
  },
  title: { color: '#fff', fontSize: 18, fontWeight: 700 },
  backBtn: {
    background: 'transparent', color: '#6b7280', border: '1px solid #374151',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  refreshBtn: {
    background: 'transparent', color: '#00d4ff', border: '1px solid #00d4ff',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  updateTime: { color: '#4b5563', fontSize: 12 },
  tabs: {
    display: 'flex', padding: '0 24px',
    borderBottom: '1px solid #1f2937', background: '#0d1117',
  },
  tab: {
    padding: '12px 20px', background: 'transparent', border: 'none',
    color: '#6b7280', cursor: 'pointer', fontSize: 14,
    borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#00d4ff', borderBottom: '2px solid #00d4ff' },
  content: { padding: 24 },
  sectionDesc: {
    color: '#6b7280', fontSize: 13, marginBottom: 16,
    padding: '10px 16px', background: '#111827',
    borderRadius: 8, borderLeft: '3px solid #00d4ff',
  },
  table: { background: '#111827', borderRadius: 12, overflow: 'hidden' },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr 100px 100px 80px 60px 60px',
    padding: '10px 16px', background: '#1f2937', gap: 8,
  },
  th: {
    color: '#6b7280', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase' as const,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr 100px 100px 80px 60px 60px',
    padding: '12px 16px', borderTop: '1px solid #1f2937',
    gap: 8, alignItems: 'center',
  },
  td: { color: '#e5e7eb', fontSize: 13 },
  locked: { textAlign: 'center' as const, padding: '80px 20px' },
  upgradeBtn: {
    background: '#00d4ff', color: '#000', border: 'none',
    padding: '12px 32px', borderRadius: 8, fontWeight: 700,
    cursor: 'pointer', fontSize: 15,
  },
  empty: { textAlign: 'center' as const, padding: '80px 20px', color: '#6b7280' },
  disclaimer: {
    textAlign: 'center' as const, padding: 16,
    color: '#374151', fontSize: 11, borderTop: '1px solid #1f2937',
  },
}
